import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  ProjectTask,
  TaskActivityLog,
  Subtask,
  CRMUser,
  ProjectTimeEntry,
  TimberProject,
  ProjectDepartment,
  CUSTOM_FRAME_DEPARTMENTS,
  DEPARTMENT_DISPLAY_LABELS,
  TaskDependencyEdge,
  DependencyEdgeType,
  UpdateDependencyPayload,
  TaskReminder,
} from '../../types';
import { recordedPhaseLabel, resolveRecordedPhase } from '../../utils/projectTimePhase';
import StatusPill from './StatusPill';
import PriorityBadge from './PriorityBadge';
import { X, Plus, Check, Trash2, Clock, User, Flag, AlertTriangle, ArrowLeft, History, MessageSquare, FlaskConical } from 'lucide-react';
import {
  ACTION_REQUIRED_EXTERNAL_SENTINEL,
  crmUserLabel,
  getActionRequiredFullLabel,
  getActionRequiredShortLabel,
  isActionRequiredExternal,
} from '../../utils/actionRequiredDisplay';
import { dependencyPayload, getDependencyEdges, wouldAddingEdgeCreateCycle } from '../../utils/taskDependencies';
import {
  countBusinessDaysInclusive,
  endDateFromStartAndInclusiveBizDays,
  impliedLagForNewDependencyEdge,
  inferFfLagBusinessDays,
  inferFsLagBusinessDays,
  inferSfLagBusinessDays,
  inferSsLagBusinessDays,
  toLocalDateInputValue,
} from '../../utils/taskSchedule';
import TaskActivityHistoryModal from './TaskActivityHistoryModal';
import TaskActionRequiredWaitChart from './TaskActionRequiredWaitChart';
import {
  computeActionRequiredWaitBreakdown,
  getActionRequiredWaitIntervals,
  getActionRequiredWaitWindow,
} from '../../utils/actionRequiredWaitBreakdown';
import type { CommitTaskDatesHint } from '../../utils/taskDateCommit';
import type { TaskDetailPanelProjectContext } from '../../utils/projectName';
import TaskSubtaskSection, { type CreateSubtaskOptions } from './TaskSubtaskSection';
import TaskReminderSection from './TaskReminderSection';
import TaskSupplierSelect, { type TaskSupplierOption } from './TaskSupplierSelect';
import { isGroupTimelineSupplierAssignableTitle } from '../../utils/groupTimelineConflicts';
import { rollupDatesFromChildren } from '../../utils/subtaskDateRollup';
import { collection, onSnapshot, query, where } from 'firebase/firestore';
import { db } from '../../utils/firebase';
import './TaskDetailPanel.css';

interface TaskDetailPanelProps {
  task: ProjectTask;
  teamMembers: CRMUser[];
  currentUserId: string;
  currentUserName: string;
  onUpdate: (id: string, updates: Partial<ProjectTask>) => void;
  onDelete: (id: string) => void;
  onLogTime?: (taskId: string, minutes: number, note: string, date?: Date) => Promise<void>;
  onUpdateTimeEntry?: (id: string, updates: Partial<ProjectTimeEntry>) => Promise<void>;
  timeEntries?: ProjectTimeEntry[];
  /** Used to infer recorded phase when an entry has no stored phase (vs work date & contractedAt). */
  project?: TimberProject | null;
  /** Project number, descriptor, PM line under the task title (from hub / portals). */
  projectContext?: TaskDetailPanelProjectContext | null;
  /** Proposal signed / deal close fallback when `project.contractedAt` is missing. */
  phaseCutoverDate?: Date | null;
  taskGroupName?: string | null;
  /** Other tasks in this project (for dependency picker). Omit when empty. */
  sameProjectTasks?: ProjectTask[];
  /** When set, start/end date edits use the same scheduling commit path as the Gantt (ripple + optional dependency prompt). */
  onCommitTaskDates?: (taskId: string, startDate: Date, endDate: Date, hint: CommitTaskDatesHint) => void | Promise<void>;
  /** When set (e.g. Project Hub), lag edits match Gantt: enforce schedule + re-infer lags from bars. */
  onUpdateDependencyLag?: (
    successorId: string,
    predecessorId: string,
    payload: UpdateDependencyPayload,
  ) => void | Promise<void>;
  onRemoveDependency?: (successorId: string, predecessorId: string) => void | Promise<void>;
  /** When set (e.g. Project Hub), adding a predecessor uses the same lag inference + enforce rules as the Gantt link drag. */
  onCreateFsDependency?: (
    predecessorId: string,
    successorId: string,
    linkType?: DependencyEdgeType,
  ) => void | Promise<void>;
  onClose: () => void;
  /** Mirrored contract checklist row — limited fields; edits sync to onboarding */
  isOnboardingChecklistTask?: boolean;
  /** Staff planning overlay: this task belongs to a simulated-under-contract project. */
  simulatedUnderContract?: boolean;
  /** When true (internal CRM on Project Hub), status row shows control to open full task activity history */
  showTaskActivityHistory?: boolean;
  /** Admin-only: expandable action required wait-time pie chart */
  showActionRequiredWaitAnalytics?: boolean;
  /** Child ProjectTask rows when this is a parent task (Project Hub). */
  childTasks?: ProjectTask[];
  /** Deal id for proposal structure picker on subtasks. */
  dealId?: string | null;
  /** Invited CNC/timber orgs for the deal — task supplier override. */
  supplierOptions?: TaskSupplierOption[];
  /** Always show the Supplier picker (Cut Timber Package / Timber Lead Time and their subtasks). */
  showSupplierField?: boolean;
  derivedSupplierLabel?: string | null;
  derivedSupplierLabelForTask?: (task: ProjectTask) => string | null;
  onCreateSubtask?: (parentId: string, title: string, opts?: CreateSubtaskOptions) => void | Promise<void>;
  onSelectSubtask?: (task: ProjectTask) => void;
  /** When viewing a subtask, navigate back to the parent panel. */
  onBackToParent?: () => void;
  parentTaskTitle?: string | null;
  /** Personal reminders for the current user on this task (Project Hub). */
  taskReminders?: TaskReminder[];
  onCreateTaskReminder?: (
    remindAt: Date,
    message?: string,
    opts?: { kind?: 'once' | 'recurring'; intervalMinutes?: number },
  ) => void | Promise<void>;
  onCancelTaskReminder?: (reminderId: string) => void | Promise<void>;
}

/** Business-day (Mon–Fri) lag implied by bars — same as stored `lagBusinessDays` when schedule matches. */
function impliedLagFromDates(task: ProjectTask, edge: TaskDependencyEdge, pred: ProjectTask | undefined): number {
  if (!pred?.id) return edge.lagBusinessDays;
  const ps = new Date(pred.startDate);
  const pe = new Date(pred.endDate);
  const ss = new Date(task.startDate);
  const se = new Date(task.endDate);
  switch (edge.type) {
    case 'SS':
      return inferSsLagBusinessDays(ps, ss);
    case 'FF':
      return inferFfLagBusinessDays(pe, se);
    case 'SF':
      return inferSfLagBusinessDays(ps, se);
    default:
      return inferFsLagBusinessDays(pe, ss);
  }
}

const TaskDetailPanel: React.FC<TaskDetailPanelProps> = ({
  task, teamMembers, currentUserId, currentUserName,
  onUpdate, onDelete, onLogTime, onUpdateTimeEntry, timeEntries = [], project = null,
  projectContext = null,
  phaseCutoverDate = null, taskGroupName = null, sameProjectTasks = [], onCommitTaskDates,
  onUpdateDependencyLag, onRemoveDependency, onCreateFsDependency, onClose,
  isOnboardingChecklistTask = false,
  simulatedUnderContract = false,
  showTaskActivityHistory = false,
  showActionRequiredWaitAnalytics = false,
  childTasks = [],
  dealId = null,
  supplierOptions = [],
  showSupplierField = false,
  derivedSupplierLabel = null,
  derivedSupplierLabelForTask,
  onCreateSubtask,
  onSelectSubtask,
  onBackToParent,
  parentTaskTitle = null,
  taskReminders = [],
  onCreateTaskReminder,
  onCancelTaskReminder,
}) => {
  const [title, setTitle] = useState(task.title);
  const [description, setDescription] = useState(task.description || '');
  const [editingTitle, setEditingTitle] = useState(false);
  const [editingDesc, setEditingDesc] = useState(false);
  const [newSubtask, setNewSubtask] = useState('');
  const [showActivity, setShowActivity] = useState(false);
  const [showDependencies, setShowDependencies] = useState(false);
  const [timeHours, setTimeHours] = useState('');
  const [timeNote, setTimeNote] = useState('');
  const [timeDate, setTimeDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [timeTime, setTimeTime] = useState(() => new Date().toLocaleTimeString('en-US', { hour12: false, hour: '2-digit', minute: '2-digit', timeZone: 'America/Chicago' }));
  const [editingEntryId, setEditingEntryId] = useState<string | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showActivityHistoryModal, setShowActivityHistoryModal] = useState(false);
  const [waitChartOpen, setWaitChartOpen] = useState(false);
  const [depError, setDepError] = useState<string | null>(null);
  const [rippleDates, setRippleDates] = useState(true);
  const [showActionMenu, setShowActionMenu] = useState(false);
  const [actionMenuStep, setActionMenuStep] = useState<'list' | 'external'>('list');
  const [externalDraft, setExternalDraft] = useState('');
  const actionDetailRef = useRef<HTMLDivElement>(null);

  useEffect(() => { setTitle(task.title); setDescription(task.description || ''); }, [task]);
  useEffect(() => { setWaitChartOpen(false); }, [task.id]);

  // Per-task activity subscription (replaces the org-wide taskActivity listener).
  const [activityLogs, setActivityLogs] = useState<TaskActivityLog[]>([]);
  useEffect(() => {
    setActivityLogs([]);
    if (!task.id) return;
    const activityQuery = query(collection(db, 'taskActivity'), where('taskId', '==', task.id));
    return onSnapshot(activityQuery, snapshot => {
      const logs = snapshot.docs
        .map(d => {
          const data = d.data();
          const createdAtRaw = data.createdAt;
          const createdAt = createdAtRaw?.toDate ? createdAtRaw.toDate()
            : createdAtRaw instanceof Date ? createdAtRaw
            : new Date(createdAtRaw || 0);
          return { id: d.id, ...data, createdAt } as TaskActivityLog;
        })
        .sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
      setActivityLogs(logs);
    }, () => setActivityLogs([]));
  }, [task.id]);

  const waitBreakdown = useMemo(
    () => computeActionRequiredWaitBreakdown(task, activityLogs, teamMembers),
    [task, activityLogs, teamMembers],
  );
  const waitIntervals = useMemo(
    () => getActionRequiredWaitIntervals(task, activityLogs, teamMembers),
    [task, activityLogs, teamMembers],
  );
  const waitWindow = useMemo(() => getActionRequiredWaitWindow(task), [task]);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (actionDetailRef.current && !actionDetailRef.current.contains(e.target as Node)) {
        setShowActionMenu(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    if (!showActionMenu) {
      setActionMenuStep('list');
      setExternalDraft('');
    }
  }, [showActionMenu]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || e.defaultPrevented) return;
      if (showActivityHistoryModal) return;
      e.preventDefault();
      if (showDeleteConfirm) {
        setShowDeleteConfirm(false);
        return;
      }
      if (showActionMenu) {
        setShowActionMenu(false);
        return;
      }
      if (editingEntryId) {
        setEditingEntryId(null);
        return;
      }
      if (editingTitle) {
        setTitle(task.title);
        setEditingTitle(false);
        return;
      }
      if (editingDesc) {
        setDescription(task.description || '');
        setEditingDesc(false);
        return;
      }
      onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [
    editingDesc,
    editingEntryId,
    editingTitle,
    onClose,
    showActionMenu,
    showActivityHistoryModal,
    showDeleteConfirm,
    task.description,
    task.title,
  ]);

  const taskActivity = useMemo(() =>
    activityLogs.filter(l => l.taskId === task.id).slice(0, 20),
    [activityLogs, task.id]
  );

  const taskTimeEntries = useMemo(() =>
    timeEntries.filter(e => e.taskId === task.id).sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()),
    [timeEntries, task.id]
  );

  const formatQtrHours = (mins: number) => {
    const hrs = Math.round(mins / 15) * 15 / 60;
    return hrs % 1 === 0 ? `${hrs}` : `${hrs}`;
  };

  const totalHours = useMemo(() => {
    const mins = taskTimeEntries.reduce((sum, e) => sum + e.minutes, 0);
    return formatQtrHours(mins);
  }, [taskTimeEntries]);

  const handleLogTime = async () => {
    const raw = parseFloat(timeHours);
    if (!raw || raw <= 0 || !onLogTime) return;
    const rounded = Math.round(raw * 4) / 4;
    const mins = rounded * 60;
    const logDate = timeDate
      ? new Date(timeDate + 'T' + (timeTime || '12:00') + ':00')
      : new Date();
    await onLogTime(task.id!, mins, timeNote.trim(), logDate);
    setTimeHours('');
    setTimeNote('');
    const now = new Date();
    setTimeDate(now.toISOString().slice(0, 10));
    setTimeTime(now.toLocaleTimeString('en-US', { hour12: false, hour: '2-digit', minute: '2-digit', timeZone: 'America/Chicago' }));
  };

  const handleAddSubtask = () => {
    if (!newSubtask.trim() || !task.id) return;
    if (onCreateSubtask) {
      void onCreateSubtask(task.id, newSubtask.trim());
      setNewSubtask('');
      return;
    }
    const existing = task.subtasks || [];
    const maxPos = existing.reduce((m, s) => Math.max(m, s.position), 0);
    const subtask: Subtask = {
      id: `sub_${Date.now()}`,
      title: newSubtask.trim(),
      completed: false,
      position: maxPos + 1,
    };
    onUpdate(task.id!, { subtasks: [...existing, subtask] });
    setNewSubtask('');
  };

  const toggleSubtask = (subId: string) => {
    const updated = (task.subtasks || []).map(s =>
      s.id === subId ? { ...s, completed: !s.completed } : s
    );
    onUpdate(task.id!, { subtasks: updated });
  };

  const deleteSubtask = (subId: string) => {
    onUpdate(task.id!, { subtasks: (task.subtasks || []).filter(s => s.id !== subId) });
  };

  const sortedChildTasks = useMemo(
    () => [...childTasks].sort((a, b) => (a.position || 0) - (b.position || 0)),
    [childTasks],
  );

  const subtaskCount = onCreateSubtask ? sortedChildTasks.length : (task.subtasks?.length ?? 0);
  const subtaskDoneCount = onCreateSubtask
    ? sortedChildTasks.filter((c) => c.status === 'completed').length
    : (task.subtasks?.filter((s) => s.completed).length ?? 0);

  const isChildTaskView = !!task.parentTaskId;

  const resolvedParentTitle = useMemo(() => {
    if (parentTaskTitle?.trim()) return parentTaskTitle.trim();
    if (!task.parentTaskId) return null;
    return sameProjectTasks.find((t) => t.id === task.parentTaskId)?.title?.trim() ?? null;
  }, [parentTaskTitle, task.parentTaskId, sameProjectTasks]);

  const isSummaryParent = !isChildTaskView && sortedChildTasks.length > 0;
  const summaryRollupDates = useMemo(
    () => (isSummaryParent ? rollupDatesFromChildren(sortedChildTasks) : null),
    [isSummaryParent, sortedChildTasks],
  );
  const scheduleStartDate = summaryRollupDates?.startDate ?? new Date(task.startDate);
  const scheduleEndDate = summaryRollupDates?.endDate ?? new Date(task.endDate);
  const durationBusinessDays = useMemo(
    () => countBusinessDaysInclusive(scheduleStartDate, scheduleEndDate),
    [scheduleStartDate, scheduleEndDate],
  );

  const formatRelativeTime = (d: Date) => {
    const diff = Date.now() - new Date(d).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'Just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    return `${days}d ago`;
  };

  const isArComplete = task.status === 'completed';
  const hasActionRequired = !!task.actionRequired && !isArComplete;
  const isExternalAr = task.actionRequired ? isActionRequiredExternal(task.actionRequired) : false;
  const isMyAction =
    hasActionRequired && !isExternalAr && task.actionRequired === currentUserId;
  const isOtherInternalAction =
    hasActionRequired && !isExternalAr && task.actionRequired !== currentUserId;

  return (
    <div
      className="pm-detail-overlay"
      onClick={(e) => {
        // Only the dimmed backdrop closes the panel. Portaled menus (StatusPill)
        // render under document.body; ignore non-backdrop targets.
        if (e.target !== e.currentTarget) return;
        onClose();
      }}
    >
      <div className="pm-detail-panel pm-scroll" onClick={e => e.stopPropagation()}>

        {isOnboardingChecklistTask && (
          <div className="pm-detail-onboarding-banner" role="status">
            Contract checklist item — checklist modal stays in sync for title and completion. Task group follows the department you set below.
          </div>
        )}

        {isChildTaskView && onBackToParent && resolvedParentTitle && (
          <nav className="pm-detail-back-nav" aria-label="Subtask navigation">
            <button type="button" onClick={onBackToParent}>
              <ArrowLeft size={14} aria-hidden /> Back to {resolvedParentTitle}
            </button>
          </nav>
        )}

        {/* Header */}
        <div className={`pm-detail-header ${isChildTaskView ? 'pm-detail-header--subtask' : ''}`}>
          <div className="pm-detail-header-left">
            {isChildTaskView && (
              <div className="pm-detail-subtask-label-row">
                <span className="pm-detail-subtask-badge">Subtask</span>
                {resolvedParentTitle && (
                  <span className="pm-detail-subtask-of">of {resolvedParentTitle}</span>
                )}
              </div>
            )}
            <div className="pm-detail-title-row">
              {editingTitle ? (
                <input
                  className="pm-detail-title-input"
                  value={title}
                  onChange={e => setTitle(e.target.value)}
                  onBlur={() => { if (title.trim()) onUpdate(task.id!, { title: title.trim() }); setEditingTitle(false); }}
                  onKeyDown={e => { if (e.key === 'Enter') { if (title.trim()) onUpdate(task.id!, { title: title.trim() }); setEditingTitle(false); } }}
                  autoFocus
                />
              ) : (
                <h2 className="pm-detail-title" onClick={() => setEditingTitle(true)}>{task.title}</h2>
              )}
              {simulatedUnderContract && (
                <span
                  className="pm-detail-sim-uc-flask"
                  title="Simulated under contract for planning — not a real contract"
                >
                  <FlaskConical size={14} strokeWidth={2.25} aria-hidden />
                  <span className="sr-only">Simulated under contract</span>
                </span>
              )}
            </div>
            {projectContext && (() => {
              const { projectNumberDisplay, descriptor, projectManagerDisplay } = projectContext;
              const numDesc = [projectNumberDisplay, descriptor].filter(Boolean).join(' · ');
              if (!numDesc && !projectManagerDisplay) return null;
              return (
                <div className="pm-detail-project-meta">
                  {numDesc ? <p className="pm-detail-project-meta-line">{numDesc}</p> : null}
                  {projectManagerDisplay ? (
                    <p className="pm-detail-project-meta-line pm-detail-project-meta-line--pm">
                      PM: {projectManagerDisplay}
                    </p>
                  ) : null}
                </div>
              );
            })()}
          </div>
          <button className="pm-detail-close" onClick={onClose}><X size={18} /></button>
        </div>

        {showActionRequiredWaitAnalytics && (
          <div className="pm-detail-wait-analytics">
            <button
              type="button"
              className="pm-detail-wait-analytics-toggle"
              onClick={() => setWaitChartOpen((open) => !open)}
              aria-expanded={waitChartOpen}
            >
              {waitChartOpen ? 'Hide action required wait breakdown' : 'Show action required wait breakdown'}
            </button>
            {waitChartOpen && (
              <TaskActionRequiredWaitChart
                slices={waitBreakdown}
                totalMs={waitWindow.totalMs}
                intervals={waitIntervals}
              />
            )}
          </div>
        )}

        {hasActionRequired && (
          <div
            className={`pm-detail-action-banner ${
              isMyAction
                ? 'pm-detail-action-banner--mine'
                : isExternalAr
                  ? 'pm-detail-action-banner--external'
                  : 'pm-detail-action-banner--other'
            }`}
            role="status"
          >
            <Flag size={14} strokeWidth={2.5} aria-hidden />
            <div className="pm-detail-action-banner-text">
              {isMyAction && (
                <>
                  Your action required
                  {task.actionRequiredNote?.trim() ? (
                    <span className="pm-detail-action-banner-note"> — {task.actionRequiredNote}</span>
                  ) : null}
                </>
              )}
              {isExternalAr && (
                <>
                  Waiting on{' '}
                  <strong>{(task.actionRequiredNote || '').trim() || 'external party'}</strong>
                </>
              )}
              {isOtherInternalAction && (
                <>
                  Waiting on{' '}
                  <strong>
                    {getActionRequiredFullLabel(task.actionRequired, task.actionRequiredNote, teamMembers)}
                  </strong>
                  {task.actionRequiredNote?.trim() ? (
                    <span className="pm-detail-action-banner-note"> — {task.actionRequiredNote}</span>
                  ) : null}
                </>
              )}
            </div>
          </div>
        )}

        {/* Meta grid */}
        <div className="pm-detail-meta">
          <div className="pm-detail-meta-row pm-detail-meta-row--status">
            <span className="pm-detail-label">Status</span>
            <div className="pm-detail-status-with-history">
              <StatusPill status={task.status} onChange={s => onUpdate(task.id!, { status: s, progress: s === 'completed' ? 100 : task.progress })} />
              {showTaskActivityHistory && (
                <button
                  type="button"
                  className="pm-detail-activity-history-btn"
                  title="View task activity history"
                  aria-label="View task activity history"
                  onClick={() => setShowActivityHistoryModal(true)}
                >
                  <History size={14} strokeWidth={2.25} />
                </button>
              )}
            </div>
          </div>
          <div className="pm-detail-meta-row">
            <span className="pm-detail-label">Priority</span>
            <PriorityBadge priority={task.priority} onChange={p => onUpdate(task.id!, { priority: p })} />
          </div>
          <div className="pm-detail-meta-row">
            <span className="pm-detail-label">Critical path</span>
            <label className="pm-detail-critical-path-toggle">
              <input
                type="checkbox"
                checked={task.onCriticalPath === true}
                onChange={(e) => onUpdate(task.id!, { onCriticalPath: e.target.checked })}
              />
              <span>Show on Gantt critical-path overlay</span>
            </label>
          </div>
          <div className="pm-detail-meta-row">
            <span className="pm-detail-label">Assignees</span>
            <div className="pm-detail-assignees">
              {task.assignedTo.map(assigneeId => {
                const member = teamMembers.find(u => u.uid === assigneeId)
                  || teamMembers.find(u => (u.email || '').trim() === assigneeId);
                const name = crmUserLabel(member)
                  || (assigneeId ? `${assigneeId.slice(0, 8)}…` : 'Unknown');
                return (
                  <span key={assigneeId} className="pm-detail-assignee-chip">
                    <User size={10} /> {name}
                    <button
                      className="pm-assignee-remove"
                      title={`Remove ${name}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        onUpdate(task.id!, { assignedTo: task.assignedTo.filter(id => id !== assigneeId) });
                      }}
                    >
                      <X size={12} />
                    </button>
                  </span>
                );
              })}
              <select
                className="pm-inline-select"
                value=""
                onChange={e => {
                  if (e.target.value && !task.assignedTo.includes(e.target.value)) {
                    onUpdate(task.id!, { assignedTo: [...task.assignedTo, e.target.value] });
                  }
                }}
              >
                <option value="">+ Add</option>
                {teamMembers.filter(u => !task.assignedTo.includes(u.uid)).map(u => (
                  <option key={u.uid} value={u.uid}>{crmUserLabel(u) || u.uid}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="pm-detail-meta-row pm-detail-meta-row--action">
            <span className="pm-detail-label">Action Required</span>
            <div className="pm-detail-action-picker" ref={actionDetailRef}>
              {task.actionRequired ? (
                <div
                  className={`pm-action-flag ${isActionRequiredExternal(task.actionRequired) ? 'pm-action-flag--external' : ''}`}
                  onClick={() => setShowActionMenu(!showActionMenu)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setShowActionMenu(!showActionMenu); }}
                  title={
                    isActionRequiredExternal(task.actionRequired)
                      ? (task.actionRequiredNote || 'External — action required')
                      : task.actionRequiredNote || 'Action required'
                  }
                >
                  <Flag size={10} />
                  {getActionRequiredShortLabel(task.actionRequired, task.actionRequiredNote, teamMembers)}
                </div>
              ) : (
                <button
                  type="button"
                  className="pm-btn-ghost pm-btn-sm pm-action-set-btn"
                  onClick={() => setShowActionMenu(!showActionMenu)}
                >
                  <Flag size={12} /> Set action required
                </button>
              )}
              {showActionMenu && (
                <div
                  className="pm-dropdown pm-detail-action-dropdown"
                  style={{ right: 0, left: 'auto', minWidth: actionMenuStep === 'external' ? 220 : 180 }}
                >
                  {actionMenuStep === 'external' ? (
                    <div className="pm-action-external-panel" onClick={e => e.stopPropagation()}>
                      <button
                        type="button"
                        className="pm-dropdown-item pm-action-external-back"
                        onClick={() => { setActionMenuStep('list'); setExternalDraft(''); }}
                      >
                        <ArrowLeft size={12} /> Back
                      </button>
                      <label className="pm-action-external-label">Who we&apos;re waiting on</label>
                      <input
                        className="pm-action-external-input"
                        value={externalDraft}
                        onChange={e => setExternalDraft(e.target.value)}
                        placeholder="e.g. county inspector, vendor"
                        autoFocus
                        onKeyDown={e => {
                          if (e.key === 'Enter' && externalDraft.trim()) {
                            onUpdate(task.id!, {
                              actionRequired: ACTION_REQUIRED_EXTERNAL_SENTINEL,
                              actionRequiredNote: externalDraft.trim(),
                            });
                            setShowActionMenu(false);
                          }
                        }}
                      />
                      <button
                        type="button"
                        className="pm-action-external-save"
                        disabled={!externalDraft.trim()}
                        onClick={() => {
                          if (!externalDraft.trim()) return;
                          onUpdate(task.id!, {
                            actionRequired: ACTION_REQUIRED_EXTERNAL_SENTINEL,
                            actionRequiredNote: externalDraft.trim(),
                          });
                          setShowActionMenu(false);
                        }}
                      >
                        Save
                      </button>
                    </div>
                  ) : (
                    <>
                      {task.actionRequired && (
                        <button
                          type="button"
                          className="pm-dropdown-item"
                          onClick={() => {
                            onUpdate(task.id!, { actionRequired: undefined, actionRequiredNote: undefined } as any);
                            setShowActionMenu(false);
                          }}
                        >
                          Clear Action Required
                        </button>
                      )}
                      {teamMembers.map(u => (
                        <button
                          key={u.uid}
                          type="button"
                          className="pm-dropdown-item"
                          onClick={() => {
                            onUpdate(task.id!, { actionRequired: u.uid, actionRequiredNote: undefined });
                            setShowActionMenu(false);
                          }}
                        >
                          <Flag size={10} style={{ color: u.role === 'supplier' ? '#047857' : '#DC2626' }} />
                          {crmUserLabel(u) || u.uid}
                          {u.role === 'supplier' ? (
                            <span className="pm-dropdown-item-meta">{u.company?.trim() || 'Supplier'}</span>
                          ) : null}
                        </button>
                      ))}
                      <button
                        type="button"
                        className="pm-dropdown-item pm-dropdown-item--external"
                        onClick={() => {
                          setActionMenuStep('external');
                          setExternalDraft(
                            task.actionRequired && isActionRequiredExternal(task.actionRequired)
                              ? (task.actionRequiredNote || '').trim()
                              : '',
                          );
                        }}
                      >
                        <Flag size={10} style={{ color: '#047857' }} />
                        External…
                      </button>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
          <div className="pm-detail-meta-row">
            <span className="pm-detail-label">Department</span>
            <select
              className="pm-inline-select"
              value={task.department}
              onChange={(e) => onUpdate(task.id!, { department: e.target.value as ProjectDepartment })}
            >
              {CUSTOM_FRAME_DEPARTMENTS.map((d) => (
                <option key={d} value={d}>
                  {DEPARTMENT_DISPLAY_LABELS[d]}
                </option>
              ))}
            </select>
          </div>
          {(showSupplierField
            || isGroupTimelineSupplierAssignableTitle(task.title)
            || isGroupTimelineSupplierAssignableTitle(parentTaskTitle ?? undefined)
            || supplierOptions.length > 0
            || (task.supplierOrgIds?.length ?? 0) > 0
            || !!derivedSupplierLabel) && (
            <div className="pm-detail-meta-row pm-detail-meta-row--supplier">
              <span className="pm-detail-label">Supplier</span>
              <TaskSupplierSelect
                options={supplierOptions}
                value={task.supplierOrgIds ?? []}
                derivedLabel={derivedSupplierLabel}
                onChange={(orgIds) => onUpdate(task.id!, { supplierOrgIds: orgIds, supplierManual: true })}
              />
            </div>
          )}
          <div className="pm-detail-meta-row">
            <span className="pm-detail-label">Start Date</span>
            <input
              type="date"
              className="pm-inline-input pm-detail-date-input"
              value={toLocalDateInputValue(scheduleStartDate)}
              disabled={isSummaryParent}
              onChange={(e) => {
                const start = e.target.value ? new Date(e.target.value + 'T12:00:00') : scheduleStartDate;
                const dur = countBusinessDaysInclusive(scheduleStartDate, scheduleEndDate);
                const end = endDateFromStartAndInclusiveBizDays(start, dur);
                if (onCommitTaskDates) void onCommitTaskDates(task.id!, start, end, { preferRipple: rippleDates, fromTaskPanel: true });
                else onUpdate(task.id!, { startDate: start, endDate: end });
              }}
            />
          </div>
          <div className="pm-detail-meta-row">
            <span className="pm-detail-label" title="Inclusive business days (weekdays). Changing this updates the due date.">
              Duration
            </span>
            <div className="pm-detail-duration-wrap">
              <input
                type="number"
                min={1}
                step={1}
                className="pm-inline-input pm-detail-duration-input"
                value={durationBusinessDays}
                disabled={isSummaryParent}
                onChange={(e) => {
                  const n = Math.max(1, parseInt(e.target.value, 10) || 1);
                  const start = new Date(scheduleStartDate);
                  const end = endDateFromStartAndInclusiveBizDays(start, n);
                  if (onCommitTaskDates) void onCommitTaskDates(task.id!, start, end, { preferRipple: rippleDates, fromTaskPanel: true });
                  else onUpdate(task.id!, { startDate: start, endDate: end });
                }}
              />
              <span className="pm-detail-duration-suffix">weekdays</span>
            </div>
          </div>
          <div className="pm-detail-meta-row">
            <span className="pm-detail-label">Due Date</span>
            <input
              type="date"
              className="pm-inline-input pm-detail-date-input"
              value={toLocalDateInputValue(scheduleEndDate)}
              disabled={isSummaryParent}
              onChange={(e) => {
                const end = e.target.value ? new Date(e.target.value + 'T12:00:00') : scheduleEndDate;
                const start = new Date(scheduleStartDate);
                if (onCommitTaskDates) void onCommitTaskDates(task.id!, start, end, { preferRipple: rippleDates, fromTaskPanel: true });
                else onUpdate(task.id!, { endDate: e.target.value ? new Date(e.target.value + 'T12:00:00') : (undefined as any) });
              }}
            />
          </div>
          {isSummaryParent && (
            <p className="pm-detail-schedule-hint pm-detail-schedule-hint--summary">
              Summary dates roll up from subtasks. Edit subtask schedules to change this range.
            </p>
          )}
          {onCommitTaskDates && !isOnboardingChecklistTask && (
            <div className="pm-detail-meta-row pm-detail-meta-row-checkbox">
              <span className="pm-detail-label">Schedule</span>
              <div className="pm-detail-schedule-hint-wrap">
                <label className="pm-detail-checkbox-label">
                  <input type="checkbox" checked={rippleDates} onChange={(e) => setRippleDates(e.target.checked)} />
                  Auto-schedule dependents
                </label>
                <p className="pm-detail-schedule-hint">
                  When this is on, date changes ripple through the schedule so linked tasks stay consistent (successors can move earlier or later).
                  Tasks with dependency links may still show a prompt (cascade vs shift whole chain). This default applies when there are no links.
                </p>
              </div>
            </div>
          )}
          <div className="pm-detail-meta-row">
            <span className="pm-detail-label">Progress</span>
            <div className="pm-detail-progress-wrap">
              <input
                type="range"
                min={0} max={100} step={5}
                value={task.progress}
                onChange={e => onUpdate(task.id!, { progress: parseInt(e.target.value) })}
                className="pm-detail-slider"
              />
              <span className="pm-detail-progress-num">{task.progress}%</span>
            </div>
          </div>
        </div>

        {/* Subtasks — above dependencies; hidden when viewing a child task */}
        {!isChildTaskView && task.id && onCreateSubtask ? (
          <div className="pm-detail-section pm-detail-section--subtasks">
            <TaskSubtaskSection
            parentTaskId={task.id}
            childTasks={sortedChildTasks}
            dealId={dealId}
            teamMembers={teamMembers}
            onUpdate={onUpdate}
            onDelete={onDelete}
            onCreateSubtask={onCreateSubtask}
            onSelectSubtask={onSelectSubtask}
            onCommitTaskDates={onCommitTaskDates}
            supplierOptions={supplierOptions}
            showSupplierField={
              showSupplierField
              || isGroupTimelineSupplierAssignableTitle(task.title)
              || isGroupTimelineSupplierAssignableTitle(parentTaskTitle ?? undefined)
            }
            derivedSupplierLabelForTask={derivedSupplierLabelForTask}
          />
          </div>
        ) : !isChildTaskView && task.id ? (
          <div className="pm-detail-section pm-detail-section--subtasks">
            <h4 className="pm-detail-section-title">
              Subtasks
              {subtaskCount > 0 && (
                <span className="pm-detail-section-count">
                  {subtaskDoneCount}/{subtaskCount}
                </span>
              )}
            </h4>
            <div className="pm-subtask-list">
              {(task.subtasks || []).map((sub) => (
                <div key={sub.id} className={`pm-subtask-item ${sub.completed ? 'pm-subtask-done' : ''}`}>
                  <button
                    type="button"
                    className={`pm-task-check pm-subtask-check ${sub.completed ? 'pm-task-checked' : ''}`}
                    onClick={() => toggleSubtask(sub.id)}
                  >
                    {sub.completed && <Check size={10} strokeWidth={3} />}
                  </button>
                  <span className="pm-subtask-title">{sub.title}</span>
                  <button type="button" className="pm-btn-ghost pm-btn-sm" onClick={() => deleteSubtask(sub.id)}>
                    <Trash2 size={11} />
                  </button>
                </div>
              ))}
              <div className="pm-subtask-add">
                <input
                  placeholder="Add subtask..."
                  value={newSubtask}
                  onChange={(e) => setNewSubtask(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleAddSubtask();
                  }}
                  className="pm-inline-input"
                />
                {newSubtask.trim() && (
                  <button type="button" className="pm-btn-ghost pm-btn-sm" onClick={handleAddSubtask}>
                    <Plus size={14} />
                  </button>
                )}
              </div>
            </div>
          </div>
        ) : null}

        {task.id && onCreateTaskReminder && onCancelTaskReminder && (
          <div className="pm-detail-section pm-detail-section--reminders">
            <TaskReminderSection
              taskId={task.id}
              projectId={task.projectId}
              taskTitle={task.title}
              currentUserId={currentUserId}
              reminders={taskReminders}
              onCreateReminder={onCreateTaskReminder}
              onCancelReminder={onCancelTaskReminder}
            />
          </div>
        )}

        {/* Description */}
        <div className="pm-detail-section">
          <h4 className="pm-detail-section-title">Description</h4>
          {editingDesc ? (
            <textarea
              className="pm-detail-textarea"
              value={description}
              onChange={e => setDescription(e.target.value)}
              onBlur={() => { onUpdate(task.id!, { description }); setEditingDesc(false); }}
              rows={4}
              autoFocus
            />
          ) : (
            <p className="pm-detail-desc" onClick={() => setEditingDesc(true)}>
              {task.description || <span className="pm-text-muted">Click to add description...</span>}
            </p>
          )}
        </div>

        {/* Comments moved — now consolidated on the group line in the task list. */}
        <div className="pm-detail-section pm-detail-comments-moved">
          <MessageSquare size={14} aria-hidden />
          <span>
            Comments are now consolidated on the <strong>group line</strong>
            {taskGroupName ? <> (<strong>{taskGroupName}</strong>)</> : null} in the task list.
            Open the group&apos;s comments button to view or add comments.
          </span>
        </div>

        {/* Time Tracking */}
        {onLogTime && (
          <div className="pm-detail-section">
            <h4 className="pm-detail-section-title">
              <Clock size={14} /> Time Tracking
              <span className="pm-detail-section-count">{totalHours}h</span>
            </h4>
            <div className="pm-time-log-form">
              <input
                type="date"
                value={timeDate}
                onChange={e => setTimeDate(e.target.value)}
                className="pm-inline-input pm-time-date-input"
              />
              <input
                type="time"
                value={timeTime}
                onChange={e => setTimeTime(e.target.value)}
                className="pm-inline-input pm-time-time-input"
              />
              <input
                type="number"
                step="0.25"
                min="0"
                placeholder="Hrs"
                value={timeHours}
                onChange={e => setTimeHours(e.target.value)}
                className="pm-inline-input pm-time-hours-input"
              />
              <input
                type="text"
                placeholder="Note"
                value={timeNote}
                onChange={e => setTimeNote(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') handleLogTime(); }}
                className="pm-inline-input pm-time-note-input"
              />
              <button className="pm-btn pm-btn-primary pm-btn-sm" onClick={handleLogTime} disabled={!timeHours || parseFloat(timeHours) <= 0}>
                <Plus size={12} /> Log
              </button>
            </div>
            {taskTimeEntries.length > 0 && (
              <div className="pm-time-entries">
                {taskTimeEntries.slice(0, 10).map(entry => {
                  const entryDate = new Date(entry.date);
                  const isEditing = editingEntryId === entry.id;
                  return (
                    <div key={entry.id} className="pm-time-entry">
                      <span className="pm-time-entry-hours">{formatQtrHours(entry.minutes)}h</span>
                      <span
                        className={`pm-time-entry-phase pm-time-entry-phase-${recordedPhaseLabel(resolveRecordedPhase(entry, project, { phaseCutoverDate: phaseCutoverDate ?? undefined, taskGroupName })).toLowerCase()}`}
                        title="Billing phase: stored on the entry, or inferred from dates / Preconstruction group"
                      >
                        {recordedPhaseLabel(resolveRecordedPhase(entry, project, { phaseCutoverDate: phaseCutoverDate ?? undefined, taskGroupName }))}
                      </span>
                      <span className="pm-time-entry-user">{entry.userName}</span>
                      {entry.note && <span className="pm-time-entry-note">{entry.note}</span>}
                      {isEditing ? (
                        <span className="pm-time-entry-edit-group">
                          <input
                            type="date"
                            className="pm-inline-input pm-time-edit-date"
                            defaultValue={entryDate.toISOString().slice(0, 10)}
                            data-entry-id={entry.id}
                          />
                          <input
                            type="time"
                            className="pm-inline-input pm-time-edit-time"
                            defaultValue={entryDate.toLocaleTimeString('en-US', { hour12: false, hour: '2-digit', minute: '2-digit', timeZone: 'America/Chicago' })}
                            autoFocus
                            data-entry-id={entry.id}
                            onKeyDown={(e) => {
                              if (e.key === 'Escape') setEditingEntryId(null);
                              if (e.key === 'Enter') {
                                const row = (e.target as HTMLElement).parentElement!;
                                const dateVal = (row.querySelector('.pm-time-edit-date') as HTMLInputElement).value;
                                const timeVal = (row.querySelector('.pm-time-edit-time') as HTMLInputElement).value;
                                if (dateVal && timeVal && onUpdateTimeEntry && entry.id) {
                                  const newDate = new Date(dateVal + 'T' + timeVal + ':00');
                                  onUpdateTimeEntry(entry.id, { date: newDate });
                                }
                                setEditingEntryId(null);
                              }
                            }}
                          />
                          <button
                            className="pm-btn-ghost pm-btn-sm"
                            title="Save"
                            onClick={() => {
                              const row = document.querySelector(`[data-entry-id="${entry.id}"]`)?.parentElement;
                              if (!row) { setEditingEntryId(null); return; }
                              const dateVal = (row.querySelector('.pm-time-edit-date') as HTMLInputElement)?.value;
                              const timeVal = (row.querySelector('.pm-time-edit-time') as HTMLInputElement)?.value;
                              if (dateVal && timeVal && onUpdateTimeEntry && entry.id) {
                                const newDate = new Date(dateVal + 'T' + timeVal + ':00');
                                onUpdateTimeEntry(entry.id, { date: newDate });
                              }
                              setEditingEntryId(null);
                            }}
                          >
                            <Check size={12} />
                          </button>
                        </span>
                      ) : (
                        <span
                          className="pm-time-entry-date"
                          title="Click to edit date/time"
                          onClick={() => setEditingEntryId(entry.id || null)}
                        >
                          {entryDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                          {' '}
                          <span className="pm-time-entry-clock">
                            {entryDate.toLocaleTimeString('en-US', { hour12: false, hour: '2-digit', minute: '2-digit', timeZone: 'America/Chicago' })}
                          </span>
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Activity — collapsible */}
        {taskActivity.length > 0 && (
          <div className="pm-detail-section">
            <button className="pm-detail-activity-toggle" onClick={() => setShowActivity(!showActivity)}>
              <Clock size={13} />
              Activity
              <span className="pm-detail-section-count">{taskActivity.length}</span>
              <span className={`pm-detail-activity-chevron ${showActivity ? 'open' : ''}`} />
            </button>
            {showActivity && (
              <div className="pm-detail-activity">
                {taskActivity.map(log => (
                  <div key={log.id} className="pm-activity-item">
                    <Clock size={12} className="pm-activity-icon" />
                    <div>
                      <span className="pm-activity-text">
                        <strong>{log.actorName}</strong> {log.action}
                        {log.field && <> <em>{log.field}</em></>}
                        {log.newValue && <> to <strong>{log.newValue}</strong></>}
                      </span>
                      <span className="pm-text-muted pm-activity-time">{formatRelativeTime(log.createdAt)}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Dependencies — collapsed by default at bottom of panel */}
        {task.id && (
          <div className="pm-detail-section pm-detail-section--dependencies">
            <button
              type="button"
              className="pm-detail-activity-toggle"
              onClick={() => setShowDependencies(!showDependencies)}
              aria-expanded={showDependencies}
            >
              Dependencies
              <span className="pm-detail-section-count">{getDependencyEdges(task).length}</span>
              <span className={`pm-detail-activity-chevron ${showDependencies ? 'open' : ''}`} />
            </button>
            {showDependencies && (
              <>
                <p className="pm-detail-deps-hint">
                  Lag is always <strong>business days</strong> (Mon–Fri), not calendar days.{' '}
                  <strong>FS</strong>/<strong>FF</strong> anchor on the predecessor <strong>end</strong>;{' '}
                  <strong>SS</strong>/<strong>SF</strong> on predecessor <strong>start</strong>.
                  Negative lag = overlap / lead.
                  {onUpdateDependencyLag && (
                    <>
                      {' '}
                      Values below match the <strong>timeline</strong> (weekday lag from bar dates).
                    </>
                  )}
                </p>
                {depError && <div className="pm-detail-deps-error">{depError}</div>}
                <div className="pm-detail-deps-list">
                  {getDependencyEdges(task).map((edge) => {
                    const pred = sameProjectTasks.find((t) => t.id === edge.predecessorId);
                    const lagShown = impliedLagFromDates(task, edge, pred);
                    return (
                      <div key={edge.predecessorId} className="pm-detail-dep-row">
                        <select
                          className="pm-inline-select pm-detail-dep-type-select"
                          aria-label={`Link type (${pred?.title || 'predecessor'})`}
                          value={edge.type}
                          disabled={!onUpdateDependencyLag}
                          onChange={(e) => {
                            const raw = e.target.value;
                            const nextType: DependencyEdgeType =
                              raw === 'FS' || raw === 'SS' || raw === 'FF' || raw === 'SF' ? raw : 'FS';
                            if (!pred?.id || !task.id) return;
                            const nextLag = impliedLagForNewDependencyEdge(pred, task, nextType);
                            setDepError(null);
                            if (onUpdateDependencyLag) {
                              void onUpdateDependencyLag(task.id, edge.predecessorId, {
                                lagBusinessDays: nextLag,
                                type: nextType,
                              });
                            } else {
                              const next = getDependencyEdges(task).map((x) =>
                                x.predecessorId === edge.predecessorId
                                  ? { ...x, type: nextType, lagBusinessDays: nextLag }
                                  : x,
                              );
                              onUpdate(task.id!, dependencyPayload(next));
                            }
                          }}
                        >
                          <option value="FS">FS</option>
                          <option value="SS">SS</option>
                          <option value="FF">FF</option>
                          <option value="SF">SF</option>
                        </select>
                        <span className="pm-detail-dep-title">{pred?.title || 'Task'}</span>
                        <label className="pm-detail-dep-lag">
                          <span className="pm-detail-dep-lag-label">Lag (bus. days)</span>
                          <input
                            type="number"
                            min={-500}
                            max={500}
                            step={1}
                            aria-label="Lag in business days"
                            value={lagShown}
                            onChange={(e) => {
                              const raw = parseInt(e.target.value, 10);
                              const v = Number.isNaN(raw) ? 0 : Math.max(-500, Math.min(500, raw));
                              setDepError(null);
                              if (onUpdateDependencyLag && task.id) {
                                void onUpdateDependencyLag(task.id, edge.predecessorId, {
                                  lagBusinessDays: v,
                                  type: edge.type,
                                });
                              } else {
                                const next = getDependencyEdges(task).map((x) =>
                                  x.predecessorId === edge.predecessorId ? { ...x, lagBusinessDays: v } : x,
                                );
                                onUpdate(task.id!, dependencyPayload(next));
                              }
                            }}
                          />
                        </label>
                        <button
                          type="button"
                          className="pm-btn-ghost pm-btn-sm"
                          title="Remove"
                          onClick={() => {
                            setDepError(null);
                            if (onRemoveDependency && task.id) {
                              void onRemoveDependency(task.id, edge.predecessorId);
                            } else {
                              const next = getDependencyEdges(task).filter((x) => x.predecessorId !== edge.predecessorId);
                              onUpdate(task.id!, dependencyPayload(next));
                            }
                          }}
                        >
                          <Trash2 size={12} />
                        </button>
                      </div>
                    );
                  })}
                </div>
                <div className="pm-detail-dep-add">
                  <select
                    className="pm-inline-select"
                    value=""
                    onChange={(e) => {
                      const predId = e.target.value;
                      e.target.value = '';
                      if (!predId || !task.id) return;
                      if (wouldAddingEdgeCreateCycle(sameProjectTasks, task.id, predId, task.projectId)) {
                        setDepError('That link would create a circular dependency.');
                        return;
                      }
                      if (getDependencyEdges(task).some((x) => x.predecessorId === predId)) return;
                      setDepError(null);
                      if (onCreateFsDependency && task.id) {
                        void onCreateFsDependency(predId, task.id, 'FS');
                      } else {
                        onUpdate(task.id!, dependencyPayload([...getDependencyEdges(task), { predecessorId: predId, type: 'FS', lagBusinessDays: 0 }]));
                      }
                    }}
                  >
                    <option value="">+ Add predecessor…</option>
                    {sameProjectTasks
                      .filter((t) => t.id && t.id !== task.id && !getDependencyEdges(task).some((e) => e.predecessorId === t.id))
                      .sort((a, b) => a.title.localeCompare(b.title))
                      .map((t) => (
                        <option key={t.id} value={t.id}>
                          {t.title}
                        </option>
                      ))}
                  </select>
                </div>
              </>
            )}
          </div>
        )}

        {/* Delete — onboarding rows remove the checklist item in projectOnboarding */}
        <div className="pm-detail-footer">
          <button className="pm-btn pm-btn-sm" style={{ color: '#DC2626', borderColor: '#FECACA' }} onClick={() => setShowDeleteConfirm(true)}>
            <Trash2 size={12} /> {isOnboardingChecklistTask ? 'Delete checklist item' : 'Delete Task'}
          </button>
        </div>
      </div>

      {showActivityHistoryModal && task.id && (
        <TaskActivityHistoryModal
          open={showActivityHistoryModal}
          onClose={() => setShowActivityHistoryModal(false)}
          taskId={task.id}
          activityLogs={activityLogs}
          teamMembers={teamMembers}
        />
      )}

      {showDeleteConfirm && (
        <div className="pm-delete-overlay" onClick={() => setShowDeleteConfirm(false)}>
          <div className="pm-delete-modal" onClick={e => e.stopPropagation()}>
            <div className="pm-delete-icon">
              <AlertTriangle size={24} />
            </div>
            <h3>{isOnboardingChecklistTask ? 'Delete checklist item' : 'Delete Task'}</h3>
            <p>
              {isOnboardingChecklistTask
                ? <>Remove <strong>{task.title}</strong> from this project&apos;s onboarding checklist? This cannot be undone.</>
                : <>Are you sure you want to delete <strong>{task.title}</strong>? This action cannot be undone.</>}
            </p>
            <div className="pm-delete-actions">
              <button className="pm-delete-cancel" onClick={() => setShowDeleteConfirm(false)}>
                Cancel
              </button>
              <button className="pm-delete-confirm" onClick={() => { onDelete(task.id!); onClose(); }}>
                <Trash2 size={13} /> Delete Permanently
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TaskDetailPanel;
